import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import styles from './Chat.module.css';
import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { ChatInput } from './ChatInput';
import { MessageProps } from './types';
import { CustomerContext } from 'contexts/CustomerContext';
import { Message } from './Message';
import { ReduxState } from 'ducks';
import { FeedbackRating } from 'models/chat';
import { AnimatedClickableDiv } from 'components/Map/AnimatedClickableDiv';
import { Loader } from './Loader';
import { useCurrentPosition } from 'hooks/useCurrentPosition';
import { selectDigitalMap } from 'ducks/universal/digitalMap';
import { useChatContext } from 'components/Chat/ChatContext';
import { WelcomeMessage } from './WelcomeMessage';
import { WelcomeMessage as WelcomeMessageType } from 'components/Chat/ChatContext';
import { useWelcomeMessage } from './util';
import { useRouter } from 'next/router';
import {
  fetchChatConversations,
  loadConversationId,
  createNewConversationId,
  createChatConversationMessage,
  updateMessageRating,
} from 'ducks/client/chatConversations';
import { ChatHistory } from './ChatHistory/ChatHistory';
import { ChatConversationMode, ChatMessageType } from 'models/chatConversation';

interface ChatWindowProps {
  digitalMapId?: string;
  onClose?: () => void;
  className?: string;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({ digitalMapId, onClose, className }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const messageRefs = React.useRef<
    {
      el: HTMLDivElement;
      isUser: boolean;
    }[]
  >([]);
  const [welcomeMessageFromHistory, setWelcomeMessageFromHistory] = React.useState<
    (WelcomeMessageType & { selectedButton: string }) | null
  >(null);
  const [messages, setMessages] = React.useState<MessageProps[]>([]);
  const [conversationId, setConversationId] = React.useState<string>('');
  const [loadingText, setLoadingText] = React.useState<string>('');
  const chatWindowRef = React.useRef<HTMLDivElement>(null);
  const chatContext = useChatContext();
  const [showHistory, setShowHistory] = React.useState(false);

  const conversations = useSelector((state: ReduxState) => state.chatConversations.conversations);
  const awaitingReply = useSelector((state: ReduxState) => state.chatConversations.awaitingReply);
  const currentConversation = conversations.find(
    (conversation) => conversation.id === conversationId
  );
  const isLoading = awaitingReply && currentConversation?.mode === ChatConversationMode.AUTO;
  const inFlightMessage = useSelector(
    (state: ReduxState) => state.chatConversations.inFlightMessage
  );
  const digitalMap = useSelector(selectDigitalMap);
  const { apiKey } = React.useContext(ApiKeyContext);
  const { customer } = React.useContext(CustomerContext);
  const { currentPositionRef } = useCurrentPosition();
  const welcomeMessage = useWelcomeMessage();
  const settings = useSelector((state: ReduxState) => state.server.settings.all);

  const router = useRouter();

  const isEmbeddedWindow = router.pathname.includes('/embed');

  const handleSendMessage = (inputMessage: string): void => {
    if (inputMessage.trim() !== '') {
      dispatch(
        createChatConversationMessage(
          apiKey,
          {
            conversation_id: conversationId,
            content: inputMessage,
            digital_map_id: digitalMapId,
            customer_id: customer?.id,
            location: currentPositionRef.current
              ? {
                  lat: currentPositionRef.current.lat(),
                  lng: currentPositionRef.current.lng(),
                }
              : undefined,
            welcome_message_text: chatContext?.action?.welcomeMessage?.text,
            button_options: chatContext?.action?.welcomeMessage?.buttons,
            selected_button: chatContext?.action?.selectedButton,
          },
          i18n.language
        )
      );
    }
  };

  const handleFeedback = (
    feedback: 'thumbsUp' | 'thumbsDown',
    chatId: string,
    currentRating?: FeedbackRating
  ) => {
    // Determine the new rating based on feedback and current state
    let newRating: FeedbackRating;

    if (feedback === 'thumbsUp') {
      newRating =
        currentRating === 'FEEDBACK_RATING_LIKED'
          ? 'FEEDBACK_RATING_UNRATED'
          : 'FEEDBACK_RATING_LIKED';
    } else {
      newRating =
        currentRating === 'FEEDBACK_RATING_UNLIKED'
          ? 'FEEDBACK_RATING_UNRATED'
          : 'FEEDBACK_RATING_UNLIKED';
    }

    dispatch(updateMessageRating(apiKey, chatId, newRating));
  };

  React.useEffect(() => {
    const conversationId = loadConversationId(digitalMapId);
    setConversationId(conversationId);
    dispatch(fetchChatConversations(apiKey, i18n.language));
  }, [apiKey, digitalMapId, i18n.language, dispatch]);

  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        dispatch(fetchChatConversations(apiKey, i18n.language));
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [apiKey, i18n.language, dispatch]);

  const handleStartNewConversation = () => {
    const newConversationId = createNewConversationId(digitalMapId);
    setConversationId(newConversationId);
    setWelcomeMessageFromHistory(null);
    setShowHistory(false);
    chatContext?.clearAction();
  };

  const handleShowHistory = () => {
    setShowHistory(true);
    dispatch(fetchChatConversations(apiKey, i18n.language));
  };

  const handleSelectThread = (threadId: string) => {
    setConversationId(threadId);
    dispatch(fetchChatConversations(apiKey, i18n.language));
    setShowHistory(false);
  };

  React.useEffect(() => {
    const messages: MessageProps[] = [];

    const currentConversation = conversations.find(
      (conversation) => conversation.id === conversationId
    );

    currentConversation?.messages.forEach((chat) => {
      if (chat.welcome_message_text) {
        setWelcomeMessageFromHistory({
          text: chat.welcome_message_text,
          buttons: chat.button_options ?? [],
          selectedButton: chat.selected_button ?? '',
        });
      }
      if (chat.message_type === ChatMessageType.LOADING_MESSAGE) {
        // Skip loading messages - they don't get added to the regular message list
      } else if (
        chat.message_type === ChatMessageType.AI_RESPONSE ||
        chat.message_type === ChatMessageType.STAFF_RESPONSE ||
        chat.message_type === ChatMessageType.SYSTEM_MESSAGE
      ) {
        messages.push({
          text: chat.content,
          isUser: false,
          showItineraryButton: chat.show_itinerary_button,
          chatId: chat.id,
          feedbackRating: chat.feedback_rating,
          messageType: chat.message_type,
        });
      } else {
        messages.push({
          text: chat.content,
          isUser: true,
          chatId: chat.id,
          messageType: chat.message_type,
        });
      }
    });

    // Check if the most recent message is a loading message
    const allMessages = currentConversation?.messages || [];
    const mostRecentMessage = allMessages[allMessages.length - 1];
    const latestLoadingText =
      mostRecentMessage?.message_type === ChatMessageType.LOADING_MESSAGE
        ? mostRecentMessage.content
        : '';

    // Only set loading text if we're currently loading, otherwise clear it
    if (isLoading) {
      setLoadingText(latestLoadingText);
    } else {
      setLoadingText('');
    }

    if (inFlightMessage) {
      messages.push({ text: inFlightMessage, isUser: true });
    }

    setMessages(messages);
  }, [
    t,
    isLoading,
    conversations,
    conversationId,
    digitalMap,
    currentPositionRef,
    inFlightMessage,
  ]);

  const messagesLengthRef = React.useRef(messages.length);

  React.useLayoutEffect(() => {
    // Only scroll when new messages are added or loading state changes, not when existing messages are updated
    const hasNewMessages = messages.length > messagesLengthRef.current;
    messagesLengthRef.current = messages.length;

    if (hasNewMessages || isLoading) {
      const userMessage = messageRefs.current.findLast((ref) => ref.isUser);
      if (userMessage) {
        userMessage.el?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [messages, isLoading]);

  return (
    <div
      className={clsx(styles.chatModal, className)}
      ref={chatWindowRef}
      style={isEmbeddedWindow ? { height: '100%', bottom: 0 } : undefined}
    >
      {onClose && !showHistory && (
        <div className={styles.header}>
          <AnimatedClickableDiv
            className={styles.closeButton}
            clickedClassName={styles.clicked}
            onClick={onClose}
          >
            <img src="/static/images/map/close.svg" className={styles.closeIcon} alt="close" />
          </AnimatedClickableDiv>
        </div>
      )}
      {showHistory ? (
        <ChatHistory
          onBack={() => setShowHistory(false)}
          onSelectThread={handleSelectThread}
          onNewThread={handleStartNewConversation}
        />
      ) : (
        <>
          <div className={styles.messagesList}>
            <div className={styles.chatHeader}>
              {settings.chat_settings?.chat_icon_url ? (
                <div className={clsx(styles.logoContainer, styles.customIcon)}>
                  <img
                    src={settings.chat_settings.chat_icon_url}
                    className={clsx(styles.logo, styles.customIcon)}
                    alt="logo"
                  />
                </div>
              ) : (
                <div className={styles.logoContainer}>
                  <img
                    src="/static/images/map/nutmeg-ai-logo.svg"
                    className={styles.logo}
                    alt="logo"
                  />
                </div>
              )}
              {settings.chat_settings?.chat_name && (
                <div className={styles.chatName}>{settings.chat_settings.chat_name}</div>
              )}
            </div>
            {welcomeMessageFromHistory ? (
              <WelcomeMessage
                className={styles.welcomeMessage}
                message={welcomeMessageFromHistory.text}
                buttons={welcomeMessageFromHistory.buttons}
                selectedButton={welcomeMessageFromHistory.selectedButton}
              />
            ) : welcomeMessage && messages.length <= 1 && !isLoading ? (
              <WelcomeMessage
                className={styles.welcomeMessage}
                message={chatContext?.action?.welcomeMessage?.text ?? welcomeMessage.message ?? ''}
                buttons={
                  chatContext?.action?.welcomeMessage?.buttons ?? welcomeMessage.buttons ?? []
                }
                selectedButton={chatContext?.action?.selectedButton ?? ''}
                onButtonClick={(welcomeMessage, button) => {
                  chatContext?.setAction({
                    welcomeMessage,
                    selectedButton: button.label,
                  });
                }}
                isLoading={welcomeMessage.isLoading}
              />
            ) : null}
            {messages.map((message, index) => {
              // Find the index of the last bot message
              const lastBotMessageIndex = messages.findLastIndex(
                (msg) => msg.messageType === ChatMessageType.AI_RESPONSE
              );
              const isLatestBotMessage =
                message.messageType === ChatMessageType.AI_RESPONSE &&
                index === lastBotMessageIndex;

              return (
                <Message
                  key={index}
                  text={message.text}
                  isUser={message.isUser ?? false}
                  onCloseChat={onClose ?? (() => {})}
                  ref={(el) => {
                    messageRefs.current[index] = {
                      el: el as HTMLDivElement,
                      isUser: message.isUser ?? false,
                    };
                  }}
                  showItineraryButton={message.showItineraryButton}
                  onFeedback={handleFeedback}
                  isLatestBotMessage={isLatestBotMessage}
                  chatId={message.chatId}
                  currentRating={message.feedbackRating}
                />
              );
            })}
            {isLoading && (
              <div className={styles.loadingArea}>
                <Loader text={loadingText} />
              </div>
            )}
          </div>
          <ChatInput
            onSendMessage={handleSendMessage}
            onStartNewConversation={handleStartNewConversation}
            onHistory={handleShowHistory}
          />
        </>
      )}
    </div>
  );
};
